import { getGame } from './49-game-catalog.mjs';
import { moveScore, scoreView } from './52-score-games.mjs';
import { reject } from './53-game-primitives.mjs';
import { createTimedBoard, timedDifficulty } from './70-timed-generators.mjs';
export const TIMED_SERIES_VERSION = 'visual-toys-3.0.0';
export const TIMED_ACTION_LIMIT = 1200;
export const TIMED_SERIES_MS = 120000;
const timedRules = {
  'gem-swap':
    'Swap neighboring charms to match three. Four in a line or crossing matches creates a Bloom blast; five in a line creates a Harvest. Swap a Harvest with a colour to clear that colour, or combine neighboring powers for a larger blast. Cascades and successful swap streaks earn bonuses.',
  'cluster-pop':
    'Tap two or more horizontally or vertically connected bubbles of one colour. Larger groups earn more points; groups of four or more build a bonus chain. Survivors fall in their own columns and replacements enter from above.',
  'soft-merge':
    'Swipe or use the direction arrows. Equal tiles combine once per slide on the six-by-six board. Consecutive merging slides and new power-of-two milestones earn bonuses. Reach the displayed target; later boards increase it up to 8192.',
  'block-fit':
    'Choose a tray shape, rotate it if useful, then tap its top-left position. Clear rows or columns for bonuses. Consecutive clearing placements build a chain; clearing the whole board earns an extra award.',
  'colour-path':
    'Start at a labelled endpoint and trace neighboring squares to its matching partner. Connect every pair without crossing and fill every square. Retrace or restart an unfinished ribbon. Later boards use fewer, longer ribbons.',
  'memory-pairs':
    'Turn over two cards. Matching pairs stay revealed; a missed pair closes after a short pause. Consecutive matches earn a bonus. Find every pair; later boards contain more pairs.',
  'tile-slide':
    'Tap a tile beside the empty space. Put numbers 1–8 in order with the space last. Later boards need more moves to solve.',
  'rotation-loop':
    'Tap pipes to rotate clockwise. Connect source to destination through every tile without leaks. Later boards have deeper scrambles; alternative complete networks also count.',
  'number-trail':
    'Tap numbers in order. Correct taps earn points and a clean chain adds a bonus. Wrong taps break the chain without losing progress. Later boards contain more numbers.',
  'spot-pair':
    'Choose the two trinkets with the same silhouette and colour. Consecutive accurate pairs earn a bonus. Later boards contain more distractors.',
};
function timedInstructions(state) {
  const target = ['gem-swap', 'cluster-pop', 'block-fit'].includes(state.gameId)
    ? ` This board needs ${state.policy.goal} ${state.gameId === 'block-fit' ? 'placements' : state.gameId === 'cluster-pop' ? 'groups' : 'productive swaps'}.`
    : '';
  return `${timedRules[state.gameId]}${target} Play for 120 active seconds across fresh boards. Cleared boards advance after a short celebration; a stalled board restarts without increasing difficulty. Your points carry forward. Pause or leaving the game stops the clock. At the highest difficulty, new boards keep the same challenge.`;
}
const validSeed = (seed) => Number.isInteger(seed) && seed >= 0 && seed <= 0xffffffff;
/** Start a deterministic timed series. Clock/consent remain session/controller
 * responsibilities; generated boards and cumulative points belong here. */
export function createTimedSeries(gameId, seed = 1) {
  const game = getGame(gameId);
  if (!game || game.kind !== 'score' || !validSeed(seed)) reject('Invalid timed game or seed.');
  const state = {
    version: TIMED_SERIES_VERSION,
    gameId,
    seed,
    rng: seed,
    moves: 0,
    score: 0,
    completed: false,
    data: {},
    difficulty: 1,
    boardIndex: 1,
    status: 'playing',
    outcome: null,
  };
  createTimedBoard(state);
  return state;
}
function checkState(state) {
  if (
    state?.version !== TIMED_SERIES_VERSION ||
    !getGame(state.gameId) ||
    !Number.isInteger(state.moves) ||
    state.moves < 0 ||
    state.moves > TIMED_ACTION_LIMIT
  )
    reject('Invalid timed state.');
}
/** Project existing game artwork and controls without exposing private answers.
 * A held board never declares the120-second run finished. */
export function timedSeriesView(state) {
  checkState(state);
  const view = scoreView({ ...state, completed: false });
  const between = state.status === 'between-boards';
  view.stage = state.policy.tier;
  view.completed = false;
  view.moveLimitReached = state.moves >= TIMED_ACTION_LIMIT;
  view.score = state.score;
  view.kind = 'score';
  view.title = getGame(state.gameId).title;
  view.instructions = timedInstructions(state);
  view.stageComplete = between;
  view.advanceAction = between ? { type: 'continue' } : null;
  view.completionReason = between ? state.outcome : null;
  if (between) {
    if (state.gameId === 'cluster-pop') view.burstHint = null;
    view.cells = view.cells.map((c) => ({ ...c, disabled: true }));
    view.options = [];
    view.autoDismiss = null;
    view.status =
      state.outcome === 'stalled'
        ? 'Board ended · a fresh board is ready.'
        : 'Board cleared · next challenge ready.';
  } else {
    view.status = `${state.difficulty > state.policy.tier ? 'Expert' : `Level ${state.policy.tier}`} · board ${state.boardIndex}`;
  }
  if (['gem-swap', 'cluster-pop', 'block-fit'].includes(state.gameId))
    view.progress = {
      current: state.data.turns,
      total: state.policy.goal,
      label: state.gameId === 'block-fit' ? 'placements' : 'moves',
    };
  if (state.gameId === 'soft-merge') {
    view.goal = `Merge to ${state.policy.target}.`;
    view.progress = {
      current: Math.max(...state.data.board),
      total: state.policy.target,
      label: 'largest tile',
    };
  }
  if (state.gameId === 'tile-slide')
    view.goal = `Arrange 1–8 · minimum ${state.policy.distance} moves.`;
  if (['tile-slide', 'spot-pair'].includes(state.gameId))
    view.progress = {
      current: between && state.outcome === 'solved' ? 1 : 0,
      total: 1,
      label: state.gameId === 'tile-slide' ? 'puzzle' : 'pair',
    };
  if (state.gameId === 'colour-path')
    view.progress = {
      current: new Set(state.data.paths.flat()).size,
      total: 16,
      label: 'linked cells',
    };
  if (view.moveLimitReached) {
    if (state.gameId === 'cluster-pop') view.burstHint = null;
    view.cells = view.cells.map((cell) => ({ ...cell, disabled: true }));
    view.options = [];
    view.advanceAction = null;
    view.stageComplete = false;
    view.autoDismiss = null;
    view.completionReason = 'move-limit';
    view.status = 'Action limit reached · the run clock continues.';
  }
  return view;
}
/** Apply a closed canonical action immutably. Continue is score-free and keeps
 * the seed stream/aggregate; solved boards advance, stalls reset local combo.
 * No stage transition reads time or trusts a client supplied difficulty. */
export function applyTimedSeries(state, action) {
  checkState(state);
  if (state.moves >= TIMED_ACTION_LIMIT) reject('Timed action limit reached.');
  if (!action || typeof action !== 'object' || Array.isArray(action))
    reject('Malformed timed action.');
  const next = structuredClone(state);
  if (action.type === 'continue') {
    if (Object.keys(action).length !== 1 || state.status !== 'between-boards')
      reject('No board to continue.');
    next.difficulty = Math.min(12, state.difficulty + (state.outcome === 'solved' ? 1 : 0));
    next.boardIndex++;
    next.status = 'playing';
    next.outcome = null;
    createTimedBoard(next);
    next.data.events = [{ type: 'stage', stage: next.policy.tier, points: 0 }];
  } else {
    if (
      state.status !== 'playing' ||
      Object.keys(action).length !== 2 ||
      !['cell', 'option'].includes(action.type) ||
      !Number.isInteger(action.index) ||
      action.index < 0
    )
      reject('Malformed timed move.');
    const view = timedSeriesView(state),
      item = (action.type === 'cell' ? view.cells : view.options)[action.index];
    if (
      !item ||
      item.disabled ||
      item.move?.type !== action.type ||
      item.move?.index !== action.index
    )
      reject('Unavailable timed move.');
    moveScore(next, action);
    const d = next.data;
    let finished = next.completed || d.stageComplete === true;
    let solved = finished;
    if (['gem-swap', 'cluster-pop', 'block-fit'].includes(state.gameId)) {
      solved = d.turns >= state.policy.goal;
      finished = finished || solved;
    }
    if (state.gameId === 'soft-merge') {
      solved = Math.max(...d.board) >= state.policy.target;
      finished = finished || solved;
    }
    if (finished) {
      next.status = 'between-boards';
      next.outcome = solved ? 'solved' : 'stalled';
      d.stageComplete = true;
      d.events = [...(d.events || []), { type: 'board-ended', outcome: next.outcome, points: 0 }];
    }
    next.completed = false;
  }
  next.moves++;
  if (!Number.isSafeInteger(next.score) || next.score < state.score) reject('Invalid timed score.');
  return next;
}
export { timedDifficulty };
